import React, { useMemo, useState } from 'react';
import { Button, Card, Typography, Tag, Space, Result, Descriptions, Modal, message, Alert } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { buildChecklist } from './materialConfig';
import { findProject, buildItemStateMap } from './mockProjects';
import CustomChecklist from './CustomChecklist';
import TargetTags from './TargetTags';
import StageBuildAction from './StageBuildAction';
import useBuildDrawer from './useBuildDrawer';
import UIDesignPanel from './UIDesignPanel';
import UIDesignUploadDrawer from './UIDesignUploadDrawer';
import useUIDesign from './useUIDesign';
import { UI_STATUS, uiStatusLabel } from './uiDesignMock';
import useAudit from './useAudit';
import { isAuditApproved, auditStatusInfo } from './auditMock';
import AuditDrawer from './AuditDrawer';

const { Title, Text } = Typography;

// 管理端项目详情：审核定制申请；审核通过后可查看/代填资料、上传 UI 设计
const CustomDetailAudit = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const location = useLocation();
    const defaultStage = location.state?.stage || null;
    const [project] = useState(() => findProject(id));
    const [itemStates, setItemStates] = useState(() => (project ? buildItemStateMap(project) : {}));
    const { buildStates, openBuild, drawerElement } = useBuildDrawer();
    const [syncedChannels, setSyncedChannels] = useState(() => new Set());
    const { getState, getStatus, upload } = useUIDesign();
    const { getStatus: getAuditStatus, getAudit, approve, reject } = useAudit();
    const [uiUpload, setUiUpload] = useState({ open: false });
    const [auditOpen, setAuditOpen] = useState(false);
    const uiTabRequested = location.state?.tab === 'ui';

    const handleSyncChannel = (key, name, isUpdate) => {
        Modal.confirm({
            title: isUpdate ? '更新企业商户号' : '同步到企业商户号',
            content: isUpdate
                ? '将按最新配置同步商户号相关配置，确认更新？'
                : `将在《企业商户号》中新增「${name}」的商户号记录，确认同步？`,
            okText: isUpdate ? '确认更新' : '确认同步',
            cancelText: '取消',
            onOk: () => {
                setSyncedChannels((prev) => new Set(prev).add(key));
                message.success(isUpdate ? `「${name}」商户号配置已更新` : `「${name}」已同步到企业商户号`);
            },
        });
    };

    const checklist = useMemo(
        () => (project ? buildChecklist(project.targets) : []),
        [project],
    );

    if (!project) {
        return (
            <div style={{ padding: 24 }}>
                <Result status="404" title="未找到该项目" subTitle="项目可能已删除，或链接无效"
                    extra={<Button type="primary" onClick={() => navigate('/app/oem-custom-audit')}>返回定制管理</Button>} />
            </div>
        );
    }

    const handleItemSave = (itemKey, patch) => {
        setItemStates((prev) => ({ ...prev, [itemKey]: { ...prev[itemKey], ...patch } }));
    };

    const uiStatus = getStatus(project.id);
    const uiTag = UI_STATUS[uiStatus];
    const uiConfirmed = uiStatus === 'confirmed';

    const auditStatus = getAuditStatus(project.id);
    const auditInfo = auditStatusInfo(auditStatus);
    const approved = isAuditApproved(auditStatus);

    return (
        <div style={{ padding: 24 }}>
            <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Space>
                    <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/app/oem-custom-audit')}>返回</Button>
                    <Title level={4} style={{ margin: 0 }}>{project.appName}</Title>
                    <Tag color="purple" style={{ border: 'none' }}>OEM 定制</Tag>
                    <Tag color={auditInfo.adminColor} style={{ border: 'none' }}>{auditInfo.adminText}</Tag>
                </Space>
                <Text type="secondary" style={{ fontSize: 12 }}>管理端 · 客服维护</Text>
            </div>

            {auditStatus === 'pending' && (
                <Alert type="info" showIcon style={{ marginBottom: 16 }}
                    message="该定制申请待审核"
                    description="审核通过后才可代填资料 / 上传 UI 设计。"
                    action={<Button size="small" type="primary" onClick={() => setAuditOpen(true)}>审核</Button>} />
            )}
            {auditStatus === 'rejected' && (
                <Alert type="warning" showIcon style={{ marginBottom: 16 }}
                    message="该定制申请已驳回"
                    description="等待企业修改申请信息后重新提交。" />
            )}

            <Card bordered={false} style={{ marginBottom: 16, borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.02)' }}>
                <Descriptions column={4} size="small">
                    <Descriptions.Item label="定制企业">{project.companyName}</Descriptions.Item>
                    <Descriptions.Item label="企业 ID">{project.companyId}</Descriptions.Item>
                    <Descriptions.Item label="定制范围"><TargetTags targets={project.targets} /></Descriptions.Item>
                    <Descriptions.Item label="定制状态"><Tag color={auditInfo.adminColor} style={{ border: 'none' }}>{auditInfo.adminText}</Tag></Descriptions.Item>
                    <Descriptions.Item label="创建时间">{project.createdAt}</Descriptions.Item>
                    <Descriptions.Item label="UI设计"><Tag color={uiTag.color} style={{ border: 'none' }}>{uiStatusLabel(uiStatus, true)}</Tag></Descriptions.Item>
                </Descriptions>
                <div style={{ marginTop: 16, paddingTop: 12, borderTop: '1px solid #f0f0f0', display: 'flex', alignItems: 'center', gap: 16 }}>
                    <Text type="secondary" style={{ fontSize: 12 }}>构建App：</Text>
                    <StageBuildAction stage="phase1" record={project} buildStates={buildStates} uiConfirmed={uiConfirmed} onOpen={openBuild} variant="button" />
                    <StageBuildAction stage="phase2" record={project} buildStates={buildStates} uiConfirmed={uiConfirmed} onOpen={openBuild} variant="button" />
                    {!uiConfirmed && <Text type="secondary" style={{ fontSize: 12 }}>（一阶段构建需客户先确认 UI 设计）</Text>}
                </div>
            </Card>

            <Card bordered={false} style={{ borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.02)' }}>
                <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 12 }}>
                    {approved
                        ? '客服视角：查看客户已填资料，可对资料代填 / 修改并保存；「UI设计」上传后由客户确认。'
                        : '客服视角：定制申请审核通过后，方可代填资料 / 上传 UI 设计；当前为只读预览。'}
                </Text>
                <CustomChecklist
                    checklist={checklist}
                    itemStates={itemStates}
                    isService
                    readOnly={!approved}
                    onItemSave={handleItemSave}
                    defaultStage={uiTabRequested ? 'ui' : defaultStage}
                    channelSync={{ enabled: true, synced: syncedChannels, onSync: handleSyncChannel }}
                    uiTab={(
                        <UIDesignPanel
                            state={getState(project.id)}
                            isService
                            onUpload={() => setUiUpload({ open: true })}
                        />
                    )}
                />
            </Card>

            {drawerElement}

            <UIDesignUploadDrawer
                open={uiUpload.open}
                record={project}
                state={getState(project.id)}
                onClose={() => setUiUpload({ open: false })}
                onSubmit={(screens, note) => upload(project.id, { screens, note })}
            />

            <AuditDrawer
                open={auditOpen}
                record={project}
                state={getAudit(project.id)}
                onClose={() => setAuditOpen(false)}
                onApprove={approve}
                onReject={reject}
            />
        </div>
    );
};

export default CustomDetailAudit;
